"use client";

import { createContext, useCallback, useContext, useEffect, useRef } from "react";
import { useConvexAuth } from "convex/react";
import { usePathname, useRouter } from "next/navigation";
import { captureClientEvent } from "@/lib/analytics/client";
import { safeInternalPath } from "@/lib/authRedirect";
import type { AuthMode } from "@/components/auth/AuthPage";

// Sign-in lives at /login and /signup (PER-234). This context survives as the
// navigation entry point so the ~16 existing call sites keep working: opening
// the "modal" now routes to the page, carrying the same redirect destination.
interface AuthModalContextType {
  openAuthModal: (mode?: AuthMode, options?: { redirectTo?: string }) => void;
}

const AuthModalContext = createContext<AuthModalContextType | undefined>(undefined);

export function useAuthModal() {
  const ctx = useContext(AuthModalContext);
  if (!ctx) {
    throw new Error("useAuthModal must be used within AuthModalProvider");
  }
  return ctx;
}

export function AuthModalProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const { isLoading, isAuthenticated } = useConvexAuth();
  const redirectInitedRef = useRef(false);

  const openAuthModal = useCallback(
    (mode: AuthMode = "signin", options?: { redirectTo?: string }) => {
      // No destination means the dashboard, not the page they clicked from.
      // Signing in from the marketing site should land you in the app; the call
      // sites that do want you back where you were pass redirectTo (or call
      // stashReturnUrl themselves, as the textbook CTAs do).
      const dest = safeInternalPath(options?.redirectTo);
      const path = mode === "signup" ? "/signup" : "/login";
      captureClientEvent("auth_modal_opened", { mode, surface: "page", dest });
      router.push(dest ? `${path}?redirect=${encodeURIComponent(dest)}` : path);
    },
    [router],
  );

  // Legacy deep links land on /?redirect=/dashboard/... (the old middleware
  // target, plus anything already sent out in email). Forward a signed-in user
  // straight there, and everyone else to the sign-up page holding that
  // destination. Skipped on the auth pages themselves, where ?redirect= is the
  // form's own state and bouncing would fight the user's choice of mode.
  useEffect(() => {
    if (typeof window === "undefined" || isLoading || redirectInitedRef.current) return;
    if (pathname === "/login" || pathname === "/signup") return;
    redirectInitedRef.current = true;
    const dest = safeInternalPath(new URLSearchParams(window.location.search).get("redirect"));
    if (!dest) return;
    if (isAuthenticated) {
      router.replace(dest);
      return;
    }
    router.replace(`/signup?redirect=${encodeURIComponent(dest)}`);
  }, [isLoading, isAuthenticated, pathname, router]);

  return (
    <AuthModalContext.Provider value={{ openAuthModal }}>{children}</AuthModalContext.Provider>
  );
}
